import React, { useRef, ReactNode } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Library, Zap, TrendingUp } from "lucide-react";

export const TextParallaxContentExample = () => {
  return (
    <div>
      {/* Subtle top transition */}
      <div className="h-32" />
      
      <TextParallaxContent
        subheading="The Problem"
        heading="Your best answers are buried.">
        <ContentBlock
          icon={<Library className="w-5 h-5" />}
          title="You're rewriting what you already know — because nothing remembers it."
          description="Your team has already written winning answers. Strong case studies. Proven approaches. Differentiators that work. But they're scattered across drives, emails, and people's heads. So every new tender starts the same way — searching, guessing, rewriting."
          supporting="Not because the answers don't exist. Because you can't find them. And every time that happens, you lose time — and weaken your chances of winning." />
      </TextParallaxContent>

      {/* Chapter divider */}
      <div className="h-px mx-auto max-w-xs bg-gradient-to-r from-transparent via-white/[0.06] to-transparent my-4" />
      
      <TextParallaxContent
        subheading="The Shift"
        heading="From three days of work to a first draft in hours.">
        <ContentBlock
          icon={<Zap className="w-5 h-5" />}
          title="Sorvaine's AI searches your past wins and writes the answer — in seconds."
          description="Sorvaine turns your scattered knowledge into a system. Upload a tender, RFP, or RFQ. Requirements are extracted. Your best answers are matched instantly. A complete first draft — built in minutes. No more digging through folders. No more starting from zero."
          supporting="Your team stops searching. And starts refining, improving, and winning." />
      </TextParallaxContent>

      {/* Chapter divider */}
      <div className="h-px mx-auto max-w-xs bg-gradient-to-r from-transparent via-white/[0.06] to-transparent my-4" />
      
      <TextParallaxContent
        subheading="The Outcome"
        heading="A stronger bid every time you use it.">
        <ContentBlock
          icon={<TrendingUp className="w-5 h-5" />}
          title="The more you use it, the better it gets."
          description="Sorvaine learns from every proposal you run. See what wins, understand what works, and build a system that gets sharper with every submission."
          supporting="The teams that win consistently aren't guessing. They're learning." />
      </TextParallaxContent>

      {/* Subtle bottom transition */}
      <div className="h-16" />
    </div>
  );
};

const IMG_PADDING = 12;

interface TextParallaxContentProps {
  subheading: string;
  heading: string;
  children: ReactNode;
}

const SECTION_HEIGHT = "115vh";

const TextParallaxContent = ({
  subheading,
  heading,
  children
}: TextParallaxContentProps) => {
  return (
    <div
      style={{
        paddingLeft: IMG_PADDING,
        paddingRight: IMG_PADDING
      }}>
      <div className="relative" style={{ height: SECTION_HEIGHT }}>
        <StickyImage />
        <OverlayCopy heading={heading} subheading={subheading} />
      </div>
      {children}
    </div>
  );
};

const StickyImage = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["end end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.8], [0.15, 0.30]);

  return (
    <motion.div
      style={{
        height: `calc(100vh - ${IMG_PADDING * 2}px)`,
        top: IMG_PADDING,
        scale,
      }}
      ref={targetRef}
      className="sticky z-0 overflow-hidden rounded-2xl ring-1 ring-white/[0.04] shadow-2xl"
    >
      {/* Subtle tinted surface — lets page background show through */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, hsl(var(--primary) / 0.06) 0%, transparent 55%), linear-gradient(180deg, hsl(0 0% 0% / 0.18) 0%, hsl(0 0% 0% / 0.08) 100%)",
        }}
      />
      {/* Gentle scroll-driven darkening for readability */}
      <motion.div
        className="absolute inset-0 bg-black"
        style={{ opacity: overlayOpacity }}
      />
      {/* Subtle inner highlight */}
      <div className="absolute inset-0 shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.04),inset_0_-80px_120px_-40px_hsl(0_0%_0%/0.3)]" />
    </motion.div>
  );
};

interface OverlayCopyProps {
  subheading: string;
  heading: string;
}

const OverlayCopy = ({ subheading, heading }: OverlayCopyProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"]
  });

  // Travel from below viewport, through center, and out the top — smooth premium drift
  const y = useTransform(scrollYProgress, [0, 1], ["35vh", "-35vh"]);
  // Fade in as it enters, full opacity through middle, gentle fade as it exits
  const opacity = useTransform(
    scrollYProgress,
    [0.05, 0.25, 0.75, 0.95],
    [0, 1, 1, 0]
  );

  return (
    <div
      ref={targetRef}
      className="absolute inset-0 pointer-events-none"
    >
      <motion.div
        style={{ y, opacity }}
        className="sticky top-0 flex h-screen w-full flex-col items-center justify-center text-white px-8"
      >
        <p className="mb-4 text-center text-[10px] tracking-[0.25em] uppercase text-primary font-medium md:text-xs">
          {subheading}
        </p>
        <p className="text-center text-3xl font-light tracking-tight md:text-6xl lg:text-7xl max-w-none md:whitespace-nowrap leading-[1.1] md:leading-[1.05] text-white">
          {heading}
        </p>
      </motion.div>
    </div>
  );
};

interface ContentBlockProps {
  icon: ReactNode;
  title: string;
  description: string;
  supporting: string;
}

const ContentBlock = ({ icon, title, description, supporting }: ContentBlockProps) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10%" }}
    transition={{ duration: 0.5, ease: "easeOut" }}
    className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:gap-12 px-8 md:px-16 lg:px-24 pt-10 pb-24 md:pt-12 md:pb-32 md:grid-cols-12"
  >
    <div className="col-span-1 md:col-span-4">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-primary/60">{icon}</span>
        <div className="w-8 h-px bg-white/10" />
      </div>
      <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white leading-[1.2]">
        {title}
      </h2>
    </div>
    <div className="col-span-1 md:col-span-8">
      {/* Invisible mirror of icon row so paragraph aligns with title, not icon */}
      <div aria-hidden className="hidden md:flex items-center gap-3 mb-6 invisible">
        <span className="text-primary/60">{icon}</span>
        <div className="w-8 h-px bg-white/10" />
      </div>
      <p className="mb-8 text-lg md:text-xl text-white/70 leading-[1.75]">
        {description}
      </p>
    </div>
  </motion.div>
);

export { TextParallaxContent, StickyImage, OverlayCopy, ContentBlock };
